<template>
  <v-container fluid class="pa-4">
    <!-- Success/Error messages at top -->
    <v-snackbar v-model="showSaved" :timeout="2000" color="success" location="top">
      Настройки сохранены
    </v-snackbar>

    <v-snackbar v-model="showError" :timeout="3000" color="error" location="top">
      Ошибка сохранения настроек
    </v-snackbar>

    <v-row>
      <v-col cols="12" md="8" lg="6">
        <v-card>
          <v-card-title class="d-flex align-center">
            <v-icon class="mr-2">mdi-bell-outline</v-icon>
            Настройки уведомлений
          </v-card-title>

          <v-card-text v-if="loading" class="text-center py-8">
            <v-progress-circular indeterminate color="primary" />
          </v-card-text>

          <v-card-text v-else>
            <!-- Master switch -->
            <div class="d-flex align-center mb-6">
              <v-switch
                v-model="settings.is_enabled"
                color="primary"
                hide-details
                :disabled="!hasAnyChannelEnabled"
              >
                <template #label>
                  <div class="d-flex align-center">
                    <v-icon :color="settings.is_enabled ? 'success' : 'grey'" class="mr-2">
                      {{ settings.is_enabled ? 'mdi-bell-ring' : 'mdi-bell-off' }}
                    </v-icon>
                    <span class="text-body-1 font-weight-medium">
                      {{ settings.is_enabled ? 'Уведомления включены' : 'Уведомления выключены' }}
                    </span>
                  </div>
                </template>
              </v-switch>
            </div>

            <v-divider class="mb-4" />

            <!-- Notification channels -->
            <div class="mb-6">
              <div class="text-subtitle-1 font-weight-medium mb-3">
                Способы уведомления
                <HelpHint section="reports" anchor="notifications" text="Почта и Telegram, частота, выбор проектов" />
              </div>

              <!-- Email -->
              <div class="d-flex align-center mb-3">
                <v-checkbox
                  v-model="settings.is_email_enabled"
                  hide-details
                  density="compact"
                >
                  <template #label>
                    <span class="mr-2">Email</span>
                  </template>
                </v-checkbox>
                <v-text-field
                  :model-value="userEmail"
                  variant="outlined"
                  density="compact"
                  hide-details
                  disabled
                  class="ml-2"
                  style="max-width: 250px;"
                />
              </div>

              <!-- Telegram -->
              <div class="d-flex align-center flex-wrap">
                <v-checkbox
                  v-model="settings.is_telegram_enabled"
                  hide-details
                  density="compact"
                  :disabled="!settings.telegram_chat_id"
                >
                  <template #label>
                    <span class="mr-2">Telegram</span>
                  </template>
                </v-checkbox>

                <!-- Show username if connected -->
                <v-chip
                  v-if="settings.telegram_chat_id"
                  size="small"
                  color="success"
                  variant="outlined"
                  class="ml-2"
                >
                  <v-icon start size="small">mdi-check</v-icon>
                  {{ settings.telegram_username ? '@' + settings.telegram_username : 'Привязан' }}
                </v-chip>

                <!-- Show "not connected" if no chat_id -->
                <v-chip
                  v-else
                  size="small"
                  color="warning"
                  variant="outlined"
                  class="ml-2"
                >
                  Не привязан
                </v-chip>
              </div>

              <!-- Telegram connection block -->
              <div class="mt-3 ml-8">
                <!-- If connected - show disconnect button -->
                <div v-if="settings.telegram_chat_id" class="d-flex align-center">
                  <v-btn
                    variant="outlined"
                    color="error"
                    size="small"
                    :loading="telegramUnlinking"
                    @click="unlinkTelegram"
                  >
                    <v-icon start>mdi-link-off</v-icon>
                    Отключить Telegram
                  </v-btn>
                </div>

                <!-- If not connected - show connect button and link -->
                <div v-else>
                  <template v-if="!telegramLinkData">
                    <div class="text-body-2 text-medium-emphasis mb-1">
                      Получайте служебные уведомления в Telegram. Подключение необязательно; его можно отключить
                      в любой момент. Для доставки Telegram получит идентификатор чата и текст уведомления.
                      Материалы и отчёты доступны в личном кабинете.
                    </div>
                    <v-checkbox v-model="telegramConsent" color="primary" density="compact" hide-details class="mb-2">
                      <template #label>
                        <span>
                          Даю
                          <router-link :to="legalLinks.consentTelegram" target="_blank" @click.stop>согласие на обработку данных для Telegram-уведомлений</router-link>.
                        </span>
                      </template>
                    </v-checkbox>
                  </template>
                  <v-btn
                    v-if="!telegramLinkData"
                    variant="outlined"
                    color="primary"
                    size="small"
                    :disabled="!telegramConsent"
                    :loading="telegramLinkLoading"
                    @click="generateTelegramLink"
                  >
                    <v-icon start>mdi-link-variant</v-icon>
                    Подключить Telegram
                  </v-btn>

                  <!-- Show link after generation -->
                  <v-alert
                    v-if="telegramLinkData"
                    type="info"
                    variant="tonal"
                    density="compact"
                    class="mt-2"
                  >
                    <div class="d-flex align-center flex-wrap">
                      <span>Перейдите по ссылке для привязки:</span>
                      <v-btn
                        :href="telegramLinkData.deep_link"
                        target="_blank"
                        variant="text"
                        color="primary"
                        size="small"
                        class="ml-2"
                      >
                        <v-icon start>mdi-open-in-new</v-icon>
                        Открыть бота
                      </v-btn>
                    </div>
                    <div class="text-caption mt-1">
                      Ссылка действительна {{ Math.floor(telegramLinkData.expires_in_seconds / 60) }} минут
                    </div>
                  </v-alert>
                </div>
              </div>
            </div>

            <v-divider class="mb-4" />

            <!-- Frequency -->
            <div class="mb-6" :class="{ 'opacity-50': !settings.is_enabled }">
              <div class="text-subtitle-1 font-weight-medium mb-3">Частота уведомлений</div>
              <v-select
                v-model="settings.frequency_hours"
                :items="frequencyOptions"
                item-title="label"
                item-value="value"
                variant="outlined"
                density="compact"
                hide-details
                :disabled="!settings.is_enabled"
              />
            </div>

            <v-divider class="mb-4" />

            <!-- Projects selection -->
            <div :class="{ 'opacity-50': !settings.is_enabled }">
              <div class="text-subtitle-1 font-weight-medium mb-3">Проекты для уведомлений</div>

              <v-alert
                v-if="projects.length === 0"
                type="info"
                variant="tonal"
                density="compact"
                class="mb-3"
              >
                У вас пока нет проектов
              </v-alert>

              <div v-else>
                <v-checkbox
                  v-model="allProjectsSelected"
                  hide-details
                  density="compact"
                  :disabled="!settings.is_enabled"
                  @update:model-value="toggleAllProjects"
                >
                  <template #label>
                    <span class="font-weight-medium">Все проекты</span>
                  </template>
                </v-checkbox>

                <v-divider class="my-2" />

                <div class="project-list" style="max-height: 300px; overflow-y: auto;">
                  <v-checkbox
                    v-for="project in projects"
                    :key="project.id"
                    :model-value="isProjectSelected(project.id)"
                    hide-details
                    density="compact"
                    :disabled="!settings.is_enabled || allProjectsSelected"
                    @update:model-value="(val) => toggleProject(project.id, val)"
                  >
                    <template #label>
                      <span>{{ project.name }}</span>
                    </template>
                  </v-checkbox>
                </div>
              </div>
            </div>
          </v-card-text>

          <!-- Save button -->
          <v-card-actions class="pa-4 pt-0">
            <v-spacer />
            <v-btn
              color="primary"
              variant="elevated"
              :loading="saving"
              :disabled="loading"
              @click="saveSettings"
            >
              Сохранить
            </v-btn>
          </v-card-actions>
        </v-card>

        <!-- Информационная рассылка: отдельное согласие, каналы выбираются отдельно -->
        <v-card class="mt-4">
          <v-card-title class="d-flex align-center">
            <v-icon class="mr-2">mdi-newspaper-variant-outline</v-icon>
            Новости сервиса
          </v-card-title>
          <v-card-text>
            <div class="text-body-2 text-medium-emphasis mb-2">
              Новости об изменениях в сервисах — по вашему желанию. Выберите email или подключённый Telegram.
              Подписку можно отключить отдельно от служебных уведомлений.
            </div>
            <v-checkbox
              v-model="newsConsent"
              label="Хочу получать информационные новости об изменениях сервиса."
              color="primary"
              density="compact"
              hide-details
            />
            <div class="text-caption text-medium-emphasis ml-10 mb-1">
              Условия рассылки — в
              <router-link :to="legalLinks.consentNews" target="_blank">согласии на рассылку</router-link>.
            </div>
            <div class="ml-8">
              <v-checkbox
                v-model="newsChannels"
                value="email"
                label="Новости по email"
                color="primary"
                density="compact"
                hide-details
                :disabled="!newsConsent"
              />
              <v-checkbox
                v-model="newsChannels"
                value="telegram"
                :label="settings.telegram_chat_id ? 'Новости в Telegram' : 'Новости в Telegram — после подключения Telegram'"
                color="primary"
                density="compact"
                hide-details
                :disabled="!newsConsent || !settings.telegram_chat_id"
              />
            </div>
            <v-alert v-if="newsMessage" :type="newsMessageType" variant="tonal" density="compact" class="mt-3">
              {{ newsMessage }}
            </v-alert>
          </v-card-text>
          <v-card-actions class="pa-4 pt-0">
            <v-spacer />
            <v-btn color="primary" variant="elevated" :loading="newsSaving" @click="saveNewsSubscription">
              Сохранить подписку
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import apiClient from "@/apiClient";
import { mapState } from "vuex";
import { LEGAL_LINKS, LEGAL_VERSIONS } from "@/config/legal";

export default {
  name: "NotificationSettings",
  data() {
    return {
      loading: true,
      saving: false,
      showSaved: false,
      showError: false,
      settings: {
        is_enabled: false,
        is_email_enabled: false,
        is_telegram_enabled: false,
        telegram_username: null,
        telegram_chat_id: null,
        frequency_hours: 24,
        project_ids: null
      },
      telegramLinkData: null,
      telegramLinkLoading: false,
      telegramUnlinking: false,
      telegramCheckInterval: null,
      telegramConsent: false,
      legalLinks: LEGAL_LINKS,
      newsConsent: false,
      newsChannels: [],
      newsSaving: false,
      newsMessage: "",
      newsMessageType: "success",
      frequencyOptions: [
        { value: 1, label: "Каждый час" },
        { value: 2, label: "Каждые 2 часа" },
        { value: 4, label: "Каждые 4 часа" },
        { value: 8, label: "Каждые 8 часов" },
        { value: 12, label: "Каждые 12 часов" },
        { value: 24, label: "Раз в сутки" },
        { value: 168, label: "Раз в неделю" }
      ],
      projects: []
    };
  },
  computed: {
    ...mapState(["userInfo"]),
    userEmail() {
      return this.userInfo?.email || "Не указан";
    },
    allProjectsSelected() {
      // null means all projects
      return this.settings.project_ids === null;
    },
    hasAnyChannelEnabled() {
      return this.settings.is_email_enabled || this.settings.is_telegram_enabled;
    }
  },
  watch: {
    // Auto-disable notifications if no channel is selected
    hasAnyChannelEnabled(newVal) {
      if (!newVal && this.settings.is_enabled) {
        this.settings.is_enabled = false;
      }
    }
  },
  methods: {
    async loadSettings() {
      this.loading = true;
      try {
        const response = await apiClient.get("/notification-settings");
        this.settings = response.data;
      } catch (error) {
        console.error("Error loading notification settings:", error);
      } finally {
        this.loading = false;
      }
    },

    async loadProjects() {
      try {
        const response = await apiClient.get("/available-projects");
        this.projects = response.data || [];
      } catch (error) {
        console.error("Error loading projects:", error);
        this.projects = [];
      }
    },

    async saveSettings() {
      if (this.saving) return;
      this.saving = true;

      try {
        // Auto-disable if no channel selected
        const isEnabled = this.hasAnyChannelEnabled ? this.settings.is_enabled : false;

        const response = await apiClient.put("/notification-settings", {
          is_enabled: isEnabled,
          is_email_enabled: this.settings.is_email_enabled,
          is_telegram_enabled: this.settings.is_telegram_enabled,
          telegram_username: this.settings.telegram_username,
          frequency_hours: this.settings.frequency_hours,
          project_ids: this.settings.project_ids
        });
        this.settings = response.data;
        this.showSaved = true;
      } catch (error) {
        console.error("Error updating notification settings:", error);
        this.showError = true;
      } finally {
        this.saving = false;
      }
    },

    isProjectSelected(projectId) {
      if (this.settings.project_ids === null) {
        return true; // All projects selected
      }
      return this.settings.project_ids.includes(projectId);
    },

    toggleProject(projectId, selected) {
      if (this.settings.project_ids === null) {
        // Was "all projects", now selecting specific ones
        this.settings.project_ids = this.projects.map(p => p.id);
      }

      if (selected) {
        if (!this.settings.project_ids.includes(projectId)) {
          this.settings.project_ids.push(projectId);
        }
      } else {
        this.settings.project_ids = this.settings.project_ids.filter(
          id => id !== projectId
        );
      }

      // If all projects are selected, set to null
      if (
        this.settings.project_ids.length === this.projects.length &&
        this.projects.every(p => this.settings.project_ids.includes(p.id))
      ) {
        this.settings.project_ids = null;
      }
    },

    toggleAllProjects(selected) {
      if (selected) {
        this.settings.project_ids = null; // null means all projects
      } else {
        this.settings.project_ids = []; // Empty array means no projects
      }
    },

    async generateTelegramLink() {
      if (this.telegramLinkLoading) return;
      this.telegramLinkLoading = true;
      this.telegramLinkData = null;

      try {
        const response = await apiClient.post("/notification-settings/telegram/generate-link", {
          consent: this.telegramConsent,
          version: LEGAL_VERSIONS.telegram
        });
        this.telegramLinkData = response.data;

        // Start polling to check if account was linked
        this.startTelegramLinkPolling();
      } catch (error) {
        console.error("Error generating Telegram link:", error);
        this.showError = true;
      } finally {
        this.telegramLinkLoading = false;
      }
    },

    startTelegramLinkPolling() {
      // Clear existing interval if any
      if (this.telegramCheckInterval) {
        clearInterval(this.telegramCheckInterval);
      }

      // Poll every 3 seconds to check if account was linked
      this.telegramCheckInterval = setInterval(async () => {
        try {
          const response = await apiClient.get("/notification-settings");
          if (response.data.telegram_chat_id) {
            // Account was linked!
            this.settings = response.data;
            this.telegramLinkData = null;
            this.stopTelegramLinkPolling();
          }
        } catch (error) {
          console.error("Error checking Telegram link status:", error);
        }
      }, 3000);

      // Stop polling after 10 minutes (link expires)
      setTimeout(() => {
        this.stopTelegramLinkPolling();
      }, 10 * 60 * 1000);
    },

    stopTelegramLinkPolling() {
      if (this.telegramCheckInterval) {
        clearInterval(this.telegramCheckInterval);
        this.telegramCheckInterval = null;
      }
    },

    async loadNewsSubscription() {
      try {
        const response = await apiClient.get("/consents");
        const news = response.data.consents.find(c => c.consent_type === "news");
        this.newsConsent = !!news;
        this.newsChannels = news?.channels || [];
      } catch (error) {
        console.error("Error loading consents:", error);
      }
    },

    async saveNewsSubscription() {
      if (this.newsSaving) return;
      this.newsSaving = true;
      this.newsMessage = "";
      try {
        // Без согласия или без каналов — отказ от рассылки
        const channels = this.newsConsent ? this.newsChannels : [];
        const response = await apiClient.put("/consents/news", { channels, version: LEGAL_VERSIONS.news });
        this.newsChannels = response.data.channels;
        this.newsConsent = this.newsChannels.length > 0;
        this.newsMessageType = "success";
        this.newsMessage = this.newsConsent
          ? "Подписка на новости сохранена."
          : "Новости отключены. Доступ к аккаунту сохранён.";
      } catch (error) {
        this.newsMessageType = "error";
        this.newsMessage = error.response?.data?.detail || "Не удалось сохранить подписку";
      } finally {
        this.newsSaving = false;
      }
    },

    async unlinkTelegram() {
      if (this.telegramUnlinking) return;
      this.telegramUnlinking = true;

      try {
        await apiClient.delete("/notification-settings/telegram");
        this.telegramConsent = false;
        // Отключение Telegram снимает и канал Telegram у новостей
        await Promise.all([this.loadSettings(), this.loadNewsSubscription()]);
        this.showSaved = true;
      } catch (error) {
        console.error("Error unlinking Telegram:", error);
        this.showError = true;
      } finally {
        this.telegramUnlinking = false;
      }
    }
  },
  async mounted() {
    await Promise.all([this.loadSettings(), this.loadProjects(), this.loadNewsSubscription()]);
  },
  beforeUnmount() {
    // Clean up polling interval
    this.stopTelegramLinkPolling();
  }
};
</script>

<style scoped>
.opacity-50 {
  opacity: 0.5;
  pointer-events: none;
}

.project-list .v-checkbox {
  margin-left: 16px;
}
</style>
